import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  Card,
  Container,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as CancelIcon,
  Send as SendIcon,
  Done as ApproveIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { PAYMENT_ROLES } from "../../common/constants";
import {
  getAllPaymentRequest,
  sendForPaymentApproval,
  cancelPayment,
  sendToSAPForPayment,
} from "./paymentAPIs";
import CustomDataGridToolbar from "../../common/customDataGridToolbar";
import { format } from "date-fns";
import { ConfirmationPopover } from "../../common/commonPopovers/confirmationPopover";
import { EditIcon } from "../../common/icons";
import { getLocalStorageItem } from "../../common/localStorageUtils";

function CommonPayment({ paymentType }) {
  const navigate = useNavigate();
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [paymentTableDetails, setPaymentTableDetails] = useState();
  const [selectedRows, setSelectedRows] = useState();
  const [actionResponse, setActionResponse] = useState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState();
  const [actionText, setActionText] = useState();

  useEffect(() => {
    if (getLocalStorageItem('role') === PAYMENT_ROLES?.PaymentViewer) {
      setFilterModel({ items: [] }); 
    } else {
      let targetStatus = "Pending_for_Approval";
      if (getLocalStorageItem('role') === PAYMENT_ROLES?.SCMEndUser) targetStatus = "Saved_Request";
      if (getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser) targetStatus = "Approved";

      setFilterModel({
        items: [
          {
            columnField: "status",
            operatorValue: "equals",
            value: targetStatus,
          },
        ],
      });
    }
  }, [getLocalStorageItem('role')]);

  const columns = [
    { field: "paymentRequestId", headerName: "Request ID", width: 100 },
    { field: "pitPono", headerName: "PO", width: 100 },
    { field: "stakeholderName", headerName: "Stakeholder Name", width: 150 },
    {
      field: "advancePaymentAmount",
      headerName: paymentType + " Payment Amount(INR)",
      width: 220,
      hide: getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(2),
    },
    {
      field: "requesterRemark",
      headerName: "Remark",
      width: 150,
      hide: getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser,
    },
    {
      field: "approveAmount",
      headerName: "Approved " + paymentType + " Payment Amount(INR)",
      width: 220,
      hide: getLocalStorageItem('role') !== PAYMENT_ROLES?.FinanceUser,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(2),
    },
    {
      field: "approverRemark",
      headerName: "Approver Remark",
      width: 180,
      hide: getLocalStorageItem('role') !== PAYMENT_ROLES?.FinanceUser,
    },
    { field: "raisedByName", headerName: "Raised By", width: 150 },
    {
      field: "raisedOn",
      headerName: "Raised On",
      width: 160,
      type: "date",
      renderCell: (params) => {
        return (
          <>
            {params?.value
              ? format(new Date(params?.value), "dd/MM/yyyy")
              : null}
          </>
        );
      },
    },
    { field: "status", headerName: "Status", width: 180 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 100,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <>
            {getLocalStorageItem('role') === PAYMENT_ROLES?.SCMEndUser && (
              <>
                {params.row?.status === "Saved_Request" ? (
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        navigate(`${paymentType.toLowerCase()}PaymentAddEditVerify`, {
                          state: { type: "Edit", selectedRow: params?.row },
                        });
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="View">
                    <IconButton
                      onClick={() => {
                        navigate(`${paymentType.toLowerCase()}PaymentAddEditVerify`, {
                          state: { type: "View", selectedRow: params?.row },
                        });
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {(getLocalStorageItem('role') === PAYMENT_ROLES?.PaymentApprover ||
              getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser ||
              getLocalStorageItem('role') === PAYMENT_ROLES?.PaymentViewer) && (
              <Tooltip title="View">
                <IconButton
                  color="primary"
                  onClick={() => {
                    navigate(`${paymentType.toLowerCase()}PaymentAddEditVerify`, {
                      state: { type: "View", selectedRow: params?.row },
                    });
                  }}
                >
                  <ViewIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const onApprovalAction = (action, text) => {
    setActionType(action);
    setActionText(text);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  function onConfirmAction() {
    setDialogOpen(false);
    const tempArr = [];
    selectedRows?.forEach((item) => {
      tempArr.push({
        id: item?.id,
        approveAmount:
          actionType !== "sendForApproval" ? item?.advancePaymentAmount : null,
        approverRemark: actionType !== "sendForApproval" ? "ok" : null,
        documents: [],
      });
    });
    sendForPaymentApproval(
      tempArr,
      actionType,
      setActionResponse,
      paymentType,
      setPaymentTableDetails
    );
  }

  const onCancel = () => {
    setActionText("Cancel");
    setDialogOpen(true);
  };

  function onConfirmCancel() {
    setDialogOpen(false);
    const tempArr = [];
    selectedRows?.forEach((item) => {
      tempArr.push(item?.id);
    });
    cancelPayment(
      tempArr,
      setActionResponse,
      paymentType,
      setPaymentTableDetails
    );
  }

  const onSendToSAP = () => {
    setActionText("Send tO SAP for payment");
    setDialogOpen(true);
  };

  function onConfirmSendToSAP() {
    setDialogOpen(false);
    const tempArr = [];
    selectedRows?.forEach((item) => {
      tempArr.push({
        paymentId: item.id,
        documents: [],
      });
    });
    sendToSAPForPayment(
      tempArr,
      setActionResponse,
      paymentType,
      setPaymentTableDetails
    );
  }

  useEffect(() => {
    getAllPaymentRequest(paymentType, setPaymentTableDetails);
  }, [getLocalStorageItem('role'), paymentType]);

  return (
    <>
      <Helmet>
        <title> {paymentType} Payment </title>
      </Helmet>
      <Container maxWidth="xxl">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          mb={1}
          justifyContent="space-between"
        >
          <Typography variant="h4" gutterBottom>
            {paymentType} Payment
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
            mb={1}
          >
            {getLocalStorageItem('role') === PAYMENT_ROLES?.SCMEndUser && (
              <>
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => {
                      navigate(
                        `${paymentType.toLowerCase()}PaymentAddEditVerify`,
                        { state: { type: "Create" } }
                      );
                    }}
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Create New
                  </Button>
                  <Button
                    onClick={() =>
                      onApprovalAction("sendForApproval", "Send For Approval")
                    }
                    variant="contained"
                    startIcon={<SendIcon />}
                  >
                    Send for Approval
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => onCancel()}
                    variant="contained"
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
            {getLocalStorageItem('role') === PAYMENT_ROLES?.PaymentApprover && (
              <>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Approve">
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<ApproveIcon />}
                      onClick={() => onApprovalAction("approve", "Approve")}
                    >
                      Approve
                    </Button>
                  </Tooltip>
                  <Button
                    onClick={() => onApprovalAction("reject", "Reject")}
                    variant="contained"
                    startIcon={<CancelIcon />}
                  >
                    Reject
                  </Button>
                </Stack>
              </>
            )}
            {getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser && (
              <>
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => onSendToSAP()}
                    variant="contained"
                    startIcon={<SendIcon />}
                  >
                    Send to SAP for payment
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
        <Card>
          <Box style={{ height: 400, width: "100%" }}>
            {paymentTableDetails && (
              <DataGrid
                rows={paymentTableDetails?.advancePayments}
                columns={columns}
                pageSize={10}         
                filterModel={filterModel}
                onFilterModelChange={(newModel) => setFilterModel(newModel)}               
                checkboxSelection={getLocalStorageItem('role') !== PAYMENT_ROLES?.PaymentViewer}               
                onSelectionModelChange={(ids) => {
                  const selectedIDs = new Set(ids);
                  const selectedRowData =
                    paymentTableDetails?.advancePayments.filter((row) =>
                      selectedIDs.has(row.id.toString())
                    );
                  setSelectedRows(selectedRowData);
                }}
                isRowSelectable={(params) =>
                  getLocalStorageItem('role') !== PAYMENT_ROLES?.PaymentViewer &&
                  params.row.status ===
                    (getLocalStorageItem('role') === PAYMENT_ROLES?.SCMEndUser
                      ? "Saved_Request"
                      : getLocalStorageItem('role') === PAYMENT_ROLES?.FinanceUser
                      ? "Approved"
                      : "Pending_for_Approval")
                }
                components={{
                  Toolbar: CustomDataGridToolbar,
                }}
                componentsProps={{
                  toolbar: {
                    rows: paymentTableDetails?.advancePayments,
                    columns: columns,
                    fileName: paymentType + "Payment.xlsx",
                  },
                }}
              />
            )}
          </Box>
        </Card>
      </Container>

      <ConfirmationPopover
        onOpen={dialogOpen}
        onClose={handleClose}
        onConfirm={
          actionText === "Cancel"
            ? onConfirmCancel
            : actionText === "Send tO SAP for payment"
            ? onConfirmSendToSAP
            : onConfirmAction
        }
        action={actionText}
      />
    </>
  );
}

export default CommonPayment;
